import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {
	Accordion, AccordionDetails, AccordionSummary,
	Autocomplete,
	Box,
	Breadcrumbs, Button, Checkbox,
	CircularProgress, FormControl, FormControlLabel, IconButton,
	InputAdornment,
	InputLabel, MenuItem,
	Select,
	Stack, Tab, Tabs,
	TextField, Tooltip,
	Typography
} from "@mui/material";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {
	GET_ALL_CUSTOM_ATTRIBUTES_SETUP,
	PARTNER_HIERARCHY_INDEX,
	PRODUCT_COMMISSIONS_DETAILS,
	PRODUCT_DETAILS,
	PRODUCT_INCENTIVE_DETAILS_INDEX,
	PUT_PRODUCT,
	PUT_PRODUCT_COMMISSION
} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {useQuery} from "@apollo/client";
import {Add, HelpOutline, OpenInNew, Remove} from "@mui/icons-material";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {cloneMutationData} from "../Utils";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {Product} from "../models/Product";
import {orderTypes, productCategories, productScoreSources, productStatuses} from "../Data";
import {TabPanel} from "../../../components/common/TabPanel";
import {ProductSellingPoint} from "../models/ProductSellingPoint";
import {generateIndexObject, openNewTab} from "../../../libraries/util/Functions";
import {ProductTestScore} from "../models/ProductTestScore";
import {GlobalState} from "../../../global/GlobalState";
import {localStorageOfObject} from "../../../libraries/util/LocalStorage";
import {ProductIncentiveDetails} from "../models/ProductIncentiveDetails";
import {ProductCommission} from "../models/ProductCommission";
import {ProductCommissionConfig} from "../models/ProductCommissionConfig";
import {DateTimePicker} from "../../../components/common/input/DatePicker";
import {DateTimeFormatter, LocalDate, LocalDateTime} from "@js-joda/core";
import {ProductCommissionShareConfig} from "../models/ProductCommissionShareConfig";
import {renderText} from "../../../components/common/TextRenderer";
import {translateFunction} from "../../../localization/TranslationService";
import {NumberField} from "../../../components/common/input/NumberField";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";
import {ProductCoverage} from "../models/ProductCoverage";
import {PartnerHierarchy} from "../models/PartnerHierarchy";
import {SubPartnerCommissions} from "../models/SubPartnerCommissions";
import {CustomAttribute} from "../models/CustomAttribute";
import {CustomAttributeSetup} from "../models/CustomAttributeSetup";
import {EventInformation} from "../models/EventInformation";

const translate = translateFunction("SureBasePortalProductsCatalog");

const tabs: string[] = [
	"info",
	"usps",
	"commission",
	"coverages",
	"custom-attributes",
	"event-information"
];

const tabsIndex = generateIndexObject(tabs);

export const newProduct = localStorageOfObject("products:products:newProduct");

export const ProductsDetails = pageAuthorizedByGroup(function () {
	const params = useParams();
	const urlId = params?.productId ?? "";
	const [tenant, supplierId, providerId, productId] = urlId?.split(":") ?? [];
	const fullProductId = `${providerId}/${productId}`;
	const fullKrn = `krn:${tenant}:product-supplier:${supplierId}:product:${fullProductId}`;
	const tabName = params?.tab;
	
	const basePath = `/products/products/${encodeURIComponent(urlId)}`;
	
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const disabled = stage !== productsEditorTargetStage;
	
	const productDetailsQuery = useQuery(PRODUCT_DETAILS, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			krn: fullKrn,
		}
	});
	
	const incentiveIndexQuery = useQuery(PRODUCT_INCENTIVE_DETAILS_INDEX, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			tenant: tenant,
		}
	});
	
	const productCommissionQuery = useQuery(PRODUCT_COMMISSIONS_DETAILS, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage,
			krn: fullKrn,
		}
	});
	
	const partnerHierarchyQuery = useQuery(PARTNER_HIERARCHY_INDEX, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: productsEditorTargetStage
		}
	});
	
	const customAttributesQuery = useQuery(GET_ALL_CUSTOM_ATTRIBUTES_SETUP, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: productsEditorTargetStage,
		}
	});
	
	const [tab, setTab] = useState<number>(tabName ? tabsIndex[tabName] ?? 0 : 0);
	const [product, setProduct] = useState<Product>();
	const [productIncentives, setProductIncentives] = useState<ProductIncentiveRecord[]>();
	const [productCommission, setProductCommission] = useState<ProductCommission>();
	const [partnerHierarchy, setPartnerHierarchy] = useState<PartnerHierarchy>();
	const [partnerHierarchyFetched, setPartnerHierarchyFetched] = useState(false);
	const [availableCustomAttributes, setAvailableCustomAttributes] = useState<CustomAttributeSetup[]>();
	
	// reset the state when stage changes
	// todo change this to proper effect that sets the state instead of clears the state, after removing the newProduct feature
	useEffect(() => {
		setProduct(undefined);
		setProductIncentives(undefined);
		setPartnerHierarchy(undefined);
	}, [stage]);
	
	if (productDetailsQuery.loading || incentiveIndexQuery.loading || productCommissionQuery.loading || partnerHierarchyQuery.loading || customAttributesQuery.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	
	const error = productDetailsQuery.error || incentiveIndexQuery.error || productCommissionQuery.error || partnerHierarchyQuery.error || customAttributesQuery.error;
	if (error)
		throw error;
	
	if (!availableCustomAttributes) {
		const attributeNodes = customAttributesQuery.data.customAttributeSetup.nodes;
		if (attributeNodes === null || attributeNodes?.length === 0)
			throw new Error(`CustomAttributeSetups not found.`);
		
		setAvailableCustomAttributes(attributeNodes);
	}
	
	if (!product) {
		const newProductValue = newProduct.get();
		if (newProductValue && newProductValue.krn === fullKrn) {
			setProduct(newProductValue);
		} else {
			const nodes = productDetailsQuery.data.products.nodes;
			if (nodes.length === 0)
				throw new Error(`Product with krn ${fullKrn} is not found.`);
			const node: Product = nodes[0];
			setProduct(fixData(node));
		}
		
		const nodes: ProductCommission[] = productCommissionQuery.data.productCommissions.nodes;
		setProductCommission(nodes.lastOrNull() ?? undefined);
		
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (!partnerHierarchyFetched) {
		const partner = partnerHierarchyQuery.data.partnerHierarchy.nodes.find((it: any) => it.clientName === tenant);
		setPartnerHierarchy(partner);
		setPartnerHierarchyFetched(true);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (!productIncentives) {
		const nodes: ProductIncentiveDetails[] = incentiveIndexQuery.data.productIncentiveDetails.nodes;
		const incentives = nodes.map(it => ({krn: it.krn, label: it.label}));
		setProductIncentives(incentives);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	function containsOnlyUniqueItems<T>(items: T[]): boolean {
		return new Set(items).size === items.length;
	}
	
	async function applyChanges() {
		if (product?.orderType !== "NOT_ORDERABLE") {
			if (product?.productTypeKrn === "krn:kizi:product-type:insurance/building-household-package" && product?.orderType !== "AFFILIATE") {
				if ((productCommission?.commissionShareConfigs?.length ?? 0) > 0) {
					throw new Error(translate("Packages cannot have commission share setup."));
				}
				if ((productCommission?.commissionConfigs?.length ?? 0) > 0) {
					throw new Error(translate("Packages cannot have commission setup."));
				}
			} else {
				if ((productCommission?.commissionShareConfigs?.length ?? 0) === 0) {
					throw new Error(translate("Products require commission share setup."));
				}
				if ((productCommission?.commissionConfigs?.length ?? 0) === 0) {
					throw new Error(translate("Products require commission setup."));
				}
			}
		}
		
		if (productCommission) {
			if (!containsOnlyUniqueItems(productCommission.commissionConfigs.map(it => it.startDate)))
				throw new Error("Multiple commission configs have the same start date.");
			if (!containsOnlyUniqueItems(productCommission.commissionShareConfigs.map(it => it.startDate)))
				throw new Error("Multiple commission share configs have the same start date.");
			
			for (const commissionShareConfig of productCommission.commissionShareConfigs) {
				checkTotalCommissions(commissionShareConfig);
			}
			
			await saveCommissionChanges(productCommission);
		}
		
		if (product)
			await saveProductChanges(product);
	}
	
	function checkTotalCommissions(commissionShareConfig: ProductCommissionShareConfig) {
		let totalPartnerCommissionFactor = commissionShareConfig.partnerCommissionFactor ?? 0;
		if (commissionShareConfig.subPartnerCommissions) {
			for (const item of commissionShareConfig.subPartnerCommissions) {
				if (typeof item.partnerCommissionFactor === "number") {
					totalPartnerCommissionFactor += item.partnerCommissionFactor;
				}
			}
		}
		if (totalPartnerCommissionFactor > 1 || totalPartnerCommissionFactor < 0)
			throw new Error(translate("Total commission share with value {totalPartnerCommissionFactor}% is invalid, the Commission share factor cannot be less than 0% or more than 100%").replace("{totalPartnerCommissionFactor}", asPercentage(totalPartnerCommissionFactor)?.toString() ?? "0"));
	}
	
	async function saveProductChanges(product: Product) {
		targetStageSafetyCheck(stage);
		if (product.coverages) {
			for (const coverage of product.coverages) {
				coverage.category = coverage?.category?.trim() ?? "";
				coverage.eventDescription = coverage?.eventDescription?.trim() ?? "";
				coverage.definition = coverage?.definition?.trim() ?? "";
			}
		}
		
		await graphQLServices.productsClient.mutate({
			mutation: PUT_PRODUCT,
			variables: {
				stage,
				product: cloneMutationData(product),
			}
		});
		newProduct.set(null);
	}
	
	async function saveCommissionChanges(productCommission: ProductCommission) {
		targetStageSafetyCheck(stage);

		// Add 'Z' to date fields if they have a value and don't already end with 'Z'
		const addZToDate = (date: string | null): string | null => {
			if (date && !date.endsWith("Z")) {
				return date + "Z";
			}
			return date;
		};

		const updatedCommission = {
			...productCommission,
			creationDateTime: addZToDate(productCommission.creationDateTime),
			lastModifiedDateTime: addZToDate(productCommission.lastModifiedDateTime),
			commissionConfigs: productCommission.commissionConfigs.map(config => ({
				...config,
				startDate: addZToDate(config.startDate) as string
			})),
			commissionShareConfigs: productCommission.commissionShareConfigs.map(config => ({
				...config,
				startDate: addZToDate(config.startDate) as string
			}))
		};

		await graphQLServices.productsClient.mutate({
			mutation: PUT_PRODUCT_COMMISSION,
			variables: {
				stage,
				productCommission: cloneMutationData(updatedCommission),
			}
		});
	}
	
	function updateUrl(data: { tab?: number; tenant?: string } = {}) {
		const _tab = data.tab ?? tab;
		const tabName = _tab > 0 ? "/" + tabs[_tab] : "";
		
		window.history.replaceState(null, "", `${basePath}${tabName}`);
	}
	
	const props = {
		product,
		setProduct,
		tab,
		productIncentives: productIncentives ?? [],
		productCommission,
		setProductCommission,
		disabled,
		partnerHierarchy,
		setPartnerHierarchy,
		availableCustomAttributes: availableCustomAttributes ?? []
	};
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products/products">{translate("Products")}</Link>
				<Typography color="text.primary">{fullProductId}</Typography>
			</Breadcrumbs>}
			center={<Stack style={{height: "100%"}}>
				<div style={{flexGrow: 0}}>
					<Box sx={{borderBottom: 1, borderColor: "divider"}}>
						<Tabs value={tab} onChange={(event, newTab) => setTab(newTab)}>
							<Tab label={translate("ProductSupplier Information")} onClick={() => updateUrl({tab: 0})}/>
							<Tab label={translate("USP’s and Warnings")} onClick={() => updateUrl({tab: 1})}/>
							<Tab label={translate("Commission")} onClick={() => updateUrl({tab: 2})}/>
							<Tab label={translate("Coverages")} onClick={() => updateUrl({tab: 3})}/>
							<Tab label={translate("Custom attributes")} onClick={() => updateUrl({tab: 4})}/>
							<Tab label={translate("Event Information")} onClick={() => updateUrl({tab: 5})}/>
						</Tabs>
					</Box>
				</div>
				<div style={{flexGrow: 1, overflow: "hidden"}}>
					<Information {...props}/>
					<Usps {...props}/>
					<Commission {...props}/>
					<Coverages {...props}/>
					<CustomAttributes {...props}/>
					<EventInformationComponent {...props}/>
				</div>
				<div style={{flexGrow: 0, padding: "10px"}}>
					<ProcessButton
						buttonProps={{
							disabled: props.disabled
						}}
						defaultText={translate("Save")}
						inProgressText={translate("Saving")}
						completedText={translate("Saved")}
						resetKeys={[product]}
						variant={"outlined"}
						onClick={() => applyChanges()}/>
				</div>
			</Stack>}
		/>
	</>;
}, ["Admin", "Product"]);

function fixData(product: Product): Product {
	if (!product.productSupplierKrn) {
		const supplierId = product.krn.split(":")[3];
		const supplierKrn = `krn:kizi:product-supplier:${supplierId}`;
		return {...product, productSupplierKrn: supplierKrn};
	}
	
	return product;
}

interface ProductIncentiveRecord {
	readonly krn: string;
	readonly label: string;
}

interface InformationProps {
	readonly tab: number;
	readonly product: Product;
	readonly setProduct: (product: Product) => void;
	readonly productIncentives: ProductIncentiveRecord[];
	readonly disabled?: boolean;
}

function nullIfEmpty(value: string): string | null {
	if (value === "")
		return null;
	return value;
}

function Information(props: InformationProps) {
	const krnParts = props.product.krn.split(":");
	const supplierKrn = `krn:kizi:product-supplier:${krnParts[3]}`;
	
	const testScores = props.product.testScores ?? [];
	const incentiveNamesByKrn = props.productIncentives.associateBy(it => it.krn, it => it.label);
	
	return <TabPanel value={props.tab} index={0}>
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2} sx={{p: 3}}>
				<div>{translate("Product Information")}</div>
				<TextField
					label={translate("Kizi Resource Name")}
					value={props.product.krn}
					variant={"standard"}
					disabled
				/>
				<TextField
					label={translate("Client Name")}
					value={props.product.clientName ?? ""}
					variant={"standard"}
					disabled
				/>
				<TextField
					label={translate("Name")}
					value={props.product.name ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setProduct({...props.product, name: event.target.value})}
				/>
				<TextField
					label={translate("Data Provider")}
					value={props.product.dataProviderKrn ?? ""}
					variant={"standard"}
					disabled
				/>
				<TextField
					label={translate("External ID")}
					value={props.product.externalId ?? ""}
					variant={"standard"}
					onChange={event => {
						props.setProduct({
							...props.product,
							externalId: event.target.value
						});
					}}
				/>
				<TextField
					label={translate("Description")}
					value={props.product.description ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setProduct({
						...props.product,
						description: nullIfEmpty(event.target.value)
					})}
				/>
				<TextField
					label={translate("Product Type")}
					value={props.product.productTypeKrn ?? ""}
					variant={"standard"}
					onChange={event => props.setProduct({
						...props.product,
						productTypeKrn: event.target.value,
						productType: event.target.value.replace("krn:kizi:product-type:", ""),
					})}
					disabled
				/>
				{/*productSupplierId*/}
				{/*productSupplierKrn*/}
				<TextField
					label={translate("Product Supplier")}
					value={supplierKrn}
					variant={"standard"}
					disabled
				/>
				
				<FormControl>
					<InputLabel id={"product-category"}>{translate("Category")}</InputLabel>
					<Select
						labelId={"product-category"}
						label={translate("Category")}
						variant={"standard"}
						value={props.product.productCategory ?? ""}
						disabled={props.disabled}
						onChange={event => props.setProduct({...props.product, productCategory: event.target.value})}
					>
						<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
						{productCategories.map(it =>
							<MenuItem key={it.toLowerCase()} value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
					</Select>
				</FormControl>
				<FormControl>
					<InputLabel id={"order-type"}>{translate("Order Type")}</InputLabel>
					<Select
						labelId={"order-type"}
						label={translate("Order Type")}
						variant={"standard"}
						value={props.product.orderType ?? ""}
						disabled={props.disabled}
						onChange={event => props.setProduct({...props.product, orderType: event.target.value})}
					>
						<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
						{orderTypes.map(it =>
							<MenuItem key={it.toLowerCase()} value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
					</Select>
				</FormControl>
				<FormControl>
					<InputLabel id={"state"}>{translate("State")}</InputLabel>
					<Select
						labelId={"state"}
						label={translate("State")}
						variant={"standard"}
						value={props.product.state ?? ""}
						disabled={props.disabled}
						onChange={event => props.setProduct({...props.product, state: event.target.value})}
					>
						<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
						{productStatuses.map(it =>
							<MenuItem key={it.toLowerCase()} value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
					</Select>
				</FormControl>
				<TextField
					label={translate("Affiliate Url")}
					value={props.product.affiliateUrl ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setProduct({
						...props.product,
						affiliateUrl: nullIfEmpty(event.target.value)
					})}
					InputProps={{
						startAdornment: props.product.affiliateUrl
							&& <IconButton onClick={() => openNewTab(props.product.affiliateUrl)}>
								<OpenInNew/>
							</IconButton>,
					}}
				/>
				<TextField
					label={translate("Insurance Card URL")}
					value={props.product.insuranceCardUrl ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setProduct({
						...props.product,
						insuranceCardUrl: nullIfEmpty(event.target.value)
					})}
					InputProps={{
						startAdornment: props.product.insuranceCardUrl
							&& <IconButton onClick={() => openNewTab(props.product.insuranceCardUrl)}>
								<OpenInNew/>
							</IconButton>,
					}}
				/>
				<TextField
					label={translate("Terms And Conditions URL")}
					value={props.product.termsAndConditionsUrl ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setProduct({
						...props.product,
						termsAndConditionsUrl: nullIfEmpty(event.target.value)
					})}
					InputProps={{
						startAdornment: props.product.termsAndConditionsUrl
							&& <IconButton onClick={() => openNewTab(props.product.termsAndConditionsUrl)}>
								<OpenInNew/>
							</IconButton>,
					}}
				/>
				
				<FormControlLabel
					control={<Checkbox
						checked={props.product.supplierGenericProduct ?? false}
						disabled={props.disabled}
						onChange={event => props.setProduct({
							...props.product,
							supplierGenericProduct: event.target.checked
						})}
					/>}
					label={translate("Is Generic Product")}
				/>
				<TextField
					label={translate("Corresponding MoneyView external id")}
					value={props.product.correspondingMoneyViewExternalId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setProduct({
						...props.product,
						correspondingMoneyViewExternalId: nullIfEmpty(event.target.value)
					})}
				/>
				<Autocomplete
					multiple
					options={props.productIncentives}
					disabled={props.disabled}
					isOptionEqualToValue={(option, value) => option.krn === value.krn}
					getOptionLabel={(option) => option.label || ""}
					value={(props.product.productIncentives ?? []).map(it => ({
						krn: it.productIncentiveDetailsKrn,
						label: incentiveNamesByKrn.get(it.productIncentiveDetailsKrn)
					}))}
					renderInput={(params) => <TextField
						{...params}
						label={translate("Product Incentives")}
						variant={"standard"}
					/>}
					onChange={(event, items) => {
						const newItems = items.length === 0 ? null : items.map(it => ({
							productIncentiveDetailsKrn: it.krn,
							active: true
						}));
						props.setProduct({...props.product, productIncentives: newItems});
					}}
				/>
				<div>{translate("Scores")}</div>
				<Stack>
					{testScores.map((it, index) => {
						return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
							<IconButton
								color={"primary"}
								disabled={props.disabled}
								onClick={() => {
									const newList = [...testScores];
									newList.splice(index, 1);
									props.setProduct({...props.product, testScores: newList});
								}}
							>
								<Remove/>
							</IconButton>
							<FormControl>
								<InputLabel id={`${index}-score-source-label`}>{translate("Source")}</InputLabel>
								<Select
									style={{width: "200px"}}
									labelId={`${index}-score-source-label`}
									label={translate("Source")}
									variant={"standard"}
									value={it.source ?? ""}
									disabled={props.disabled}
									onChange={event => {
										const newItem: ProductTestScore = {...it, source: event.target.value};
										const newList = [...testScores];
										newList[index] = newItem;
										props.setProduct({...props.product, testScores: newList});
									}}
								>
									<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
									{productScoreSources.map(it =>
										<MenuItem key={it.toLowerCase()} value={it}>{translate(it)}</MenuItem>)}
								</Select>
							</FormControl>
							<TextField
								variant={"standard"}
								label={translate("Score")}
								type={"number"}
								value={it.score ?? ""}
								style={{flexGrow: 1}}
								inputProps={{
									step: "0.1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onChange={event => {
									const numberValue = event.target.value ? Number(event.target.value) : undefined;
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									const newItem: ProductTestScore = {...it, score: numberValue!};
									const newList = [...testScores];
									newList[index] = newItem;
									props.setProduct({...props.product, testScores: newList});
								}}
							/>
						</Stack>;
					})}
				</Stack>
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...testScores, {} as ProductTestScore];
							props.setProduct({...props.product, testScores: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>
		</div>
	</TabPanel>;
}

interface ProductTabProps {
	readonly tab: number;
	readonly product: Product;
	readonly setProduct: (product: Product) => void;
	readonly disabled?: boolean;
	readonly availableCustomAttributes: CustomAttributeSetup[];
}

function Usps(props: ProductTabProps) {
	const sellingPoints = props.product.sellingPoints ?? [];
	
	return <TabPanel value={props.tab} index={1}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("USP’s and Warnings")}</div>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{sellingPoints.map((it, index) => {
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...sellingPoints];
								newList.splice(index, 1);
								props.setProduct({...props.product, sellingPoints: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<TextField
							variant={"standard"}
							label={translate("Description")}
							value={it.description ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, description: event.target.value};
								const newList = [...sellingPoints];
								newList[index] = newItem;
								props.setProduct({...props.product, sellingPoints: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...sellingPoints];
								newList[index] = newItem;
								props.setProduct({...props.product, sellingPoints: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...sellingPoints, {} as ProductSellingPoint];
							props.setProduct({...props.product, sellingPoints: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

function Coverages(props: ProductTabProps) {
	const items = props.product.coverages ?? [];
	
	return <TabPanel value={props.tab} index={3}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Coverages")}</div>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{items.map((it, index) => {
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...items];
								newList.splice(index, 1);
								props.setProduct({...props.product, coverages: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<TextField
							variant={"standard"}
							label={translate("Category")}
							value={it.category ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, category: event.target.value ?? null};
								const newList = [...items];
								newList[index] = newItem;
								props.setProduct({...props.product, coverages: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Event")}
							value={it.eventDescription ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, eventDescription: event.target.value ?? null};
								const newList = [...items];
								newList[index] = newItem;
								props.setProduct({...props.product, coverages: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Definition")}
							multiline
							value={it.definition ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, definition: event.target.value};
								const newList = [...items];
								newList[index] = newItem;
								props.setProduct({...props.product, coverages: newList});
							}}
						/>
						<NumberField
							variant={"standard"}
							label={translate("Coverage Days")}
							value={it.coverageDays ?? ""}
							placeholder={"0"}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onValueChange={value => {
								const newItem = {...it, coverageDays: value ?? 0};
								const newList = [...items];
								newList[index] = newItem;
								props.setProduct({...props.product, coverages: newList});
							}}
						/>
					
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...items, {} as ProductCoverage];
							props.setProduct({...props.product, coverages: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

function CustomAttributes(props: ProductTabProps) {
	const customAttributes = props.product.customAttributes ?? [];
	
	return <TabPanel value={props.tab} index={4}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Custom attributes")}</div>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{customAttributes.map((it, index) => {
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...customAttributes];
								newList.splice(index, 1);
								props.setProduct({...props.product, customAttributes: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<Select
							label={translate("Custom attributes")}
							variant={"standard"}
							style={{flexGrow: 1}}
							value={it.description ?? ""}
							onChange={event => {
								const newItem = {...it, description: event.target.value};
								const newList = [...customAttributes];
								newList[index] = newItem;
								props.setProduct({...props.product, customAttributes: newList});
							}}
						>
							{it.description && <MenuItem key={it.description} value={it.description}>{it.description}</MenuItem>}
							{props.availableCustomAttributes?.filter((x) => {
								return !props.product.customAttributes?.find(y => y.description === x.description);
							})?.map(x =>
								<MenuItem key={x.description} value={x.description}>{x.description}</MenuItem>)}
						</Select>
						
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...customAttributes];
								newList[index] = newItem;
								props.setProduct({...props.product, customAttributes: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				{props.availableCustomAttributes.length !== props.product.customAttributes?.length && <div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...customAttributes, {} as CustomAttribute];
							props.setProduct({...props.product, customAttributes: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>}
			</Stack>}
		/>
	</TabPanel>;
}

function EventInformationComponent(props: ProductTabProps) {
	const eventInformationList = props.product.eventInformation ?? [];
	
	return <TabPanel value={props.tab} index={5}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Event Information")}</div>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{eventInformationList.map((it, index) => {
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...eventInformationList];
								newList.splice(index, 1);
								props.setProduct({...props.product, eventInformation: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<TextField
							variant={"standard"}
							label={translate("Category")}
							value={it.category ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, category: event.target.value};
								const newList = [...eventInformationList];
								newList[index] = newItem;
								props.setProduct({...props.product, eventInformation: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Name")}
							value={it.name ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, name: event.target.value};
								const newList = [...eventInformationList];
								newList[index] = newItem;
								props.setProduct({...props.product, eventInformation: newList});
							}}
						/>
						
						<TextField
							variant={"standard"}
							label={translate("Definition")}
							value={it.definition ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, definition: event.target.value};
								const newList = [...eventInformationList];
								newList[index] = newItem;
								props.setProduct({...props.product, eventInformation: newList});
							}}
						/>
						
						<TextField
							variant={"standard"}
							label={translate("Duration")}
							value={it.duration ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, duration: event.target.value};
								const newList = [...eventInformationList];
								newList[index] = newItem;
								props.setProduct({...props.product, eventInformation: newList});
							}}
						/>
					
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...eventInformationList, {id: crypto.randomUUID()} as EventInformation];
							props.setProduct({...props.product, eventInformation: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

interface CommissionProps {
	readonly tab: number;
	readonly product: Product;
	readonly productCommission: ProductCommission | undefined;
	readonly setProductCommission: (commission: ProductCommission) => void;
	readonly partnerHierarchy: PartnerHierarchy | undefined;
	readonly setPartnerHierarchy: (partnerHierarchy: PartnerHierarchy) => void;
	readonly disabled?: boolean;
}

function parseDateNullable(text: string | null): LocalDateTime | null {
	return text ? parseDate(text) : null;
}

function parseDate(text: string): LocalDateTime {
	return LocalDateTime.parse(text.endsWith("Z") ? text.substring(0, text.length - 1) : text);
}

function asPercentage(value: number | null): number | null {
	if (typeof value === "number")
		return value * 100;
	return null;
}

function Commission(props: CommissionProps) {
	const productCommission: ProductCommission = props.productCommission ?? defaultProductCommission(props.product.krn);
	const commissionUntilIndex = computeUntilIndexFromObjects(productCommission.commissionConfigs);
	const commissionShareUntilIndex = computeUntilIndexFromObjects(productCommission.commissionShareConfigs);
	
	return <TabPanel value={props.tab} index={2}>
		<Stack style={{height: "100%", overflow: "auto"}} spacing={2}>
			<TextField
				label={translate("Client Name")}
				value={translate(props.product.clientName)}
				variant={"standard"}
				disabled
			/>
			<Typography variant={"h5"}>{translate("Commission")}</Typography>
			{productCommission.commissionConfigs.map((commissionConfig, index) => {
				function setCommissionConfig(newConfig: ProductCommissionConfig) {
					const newList = [...productCommission.commissionConfigs];
					newList[index] = newConfig;
					props.setProductCommission({...productCommission, commissionConfigs: newList});
				}
				
				const commissionNetPremiumFactor = asPercentage(commissionConfig.commissionNetPremiumFactor);
				const commissionNetPremiumFactorPerBillablePerson = asPercentage(commissionConfig.commissionNetPremiumFactorPerBillablePerson);
				const commissionAmountPerMonth = commissionConfig.commissionAmountPerMonth ?? null;
				const numberOfMonths = commissionConfig.numberOfMonths ?? null;
				const commissionAmountPerBillablePerson = commissionConfig.commissionAmountPerBillablePerson ?? null;
				const commissionAmountFixedFee = commissionConfig.commissionAmountFixedFee ?? null;
				const renewalCommissionInstallmentAmount = commissionConfig.renewalCommissionInstallmentAmount ?? null;
				const renewalCommissionInstallmentNumberOfMonths = commissionConfig.renewalCommissionInstallmentNumberOfMonths ?? null;
				const renewalCommissionInstallmentAmountLink=commissionConfig.renewalCommissionInstallmentAmountLink ?? null;
				const header = `${translate("From")} ${commissionConfig.startDate} ${translate("until")} ${commissionUntilIndex.get(commissionConfig.startDate) ?? translate("the end of time")}`;
				
				return <Accordion key={`commission-${index}`}>
					<AccordionSummary>{header}</AccordionSummary>
					<AccordionDetails>
						<Stack spacing={2}>
							<DateTimePicker
								textFieldProps={{
									style: {width: "250px"}
								}}
								label={translate("Start Date")}
								variant={"standard"}
								value={parseDateNullable(commissionConfig.startDate)}
								inputFormat={GlobalState.userPreferences.inputDateTimePatternOrDefault}
								disabled={props.disabled}
								onValueChange={value => {
									const valueString = value ? DateTimeFormatter.ISO_LOCAL_DATE_TIME.format(value) : null as unknown as string;
									setCommissionConfig({...commissionConfig, startDate: valueString});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Net premium percentage")}
								variant={"standard"}
								placeholder={"0"}
								value={commissionNetPremiumFactor ?? ""}
								InputLabelProps={{
									shrink: true
								}}
								InputProps={{
									endAdornment: <>
										<InputAdornment position={"end"}>%</InputAdornment>
										<InputTooltip
											text={translate("This the percentage of the net premium.\nIn Dutch it is translated to: Provisie Percentage.")}/>
									</>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
									style: {textAlign: "right"}
								}}
								disabled={props.disabled}
								onValueChange={value => {
									const numberValue = value ? value / 100 : null;
									setCommissionConfig({
										...commissionConfig,
										commissionNetPremiumFactor: numberValue
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Net premium percentage per billable person")}
								variant={"standard"}
								placeholder={"0"}
								value={commissionNetPremiumFactorPerBillablePerson ?? ""}
								InputLabelProps={{
									shrink: true
								}}
								InputProps={{
									endAdornment: <>
										<InputAdornment position={"end"}>%</InputAdornment>
										<InputTooltip
											text={translate("This the percentage of the net Premium per every insured person above 18 years.\nSpecifically for healthcare products one order might consist of multiple insured persons above 18 years.")}/>
									</>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
									style: {textAlign: "right"}
								}}
								disabled={props.disabled}
								onValueChange={value => {
									const numberValue = value ? value / 100 : null;
									setCommissionConfig({
										...commissionConfig,
										commissionNetPremiumFactorPerBillablePerson: numberValue
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Flat commission amount per month")}
								variant={"standard"}
								placeholder={"0"}
								value={commissionAmountPerMonth ?? ""}
								InputProps={{
									startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
									endAdornment: <InputTooltip
										text={translate("The fixed amount of commission we earn for this product per month.")}/>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onValueChange={value => {
									setCommissionConfig({
										...commissionConfig,
										commissionAmountPerMonth: value
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Number of months")}
								variant={"standard"}
								placeholder={"0"}
								fractionDigits={0}
								value={numberOfMonths ?? ""}
								InputLabelProps={{
									shrink: true
								}}
								InputProps={{
									endAdornment: <InputTooltip
										text={translate("This is the duration (in months) that this specific product is active in our insurance portfolio.\nThe duration is required when the net premium percentage or flat commission amount per month is filled in.")}/>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onValueChange={value => {
									const numberValue = value ? Number(value) : null;
									setCommissionConfig({
										...commissionConfig,
										numberOfMonths: numberValue
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate(translate("Flat commission amount per billable person"))}
								variant={"standard"}
								placeholder={"0"}
								value={commissionAmountPerBillablePerson ?? ""}
								InputProps={{
									startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
									endAdornment: <InputTooltip
										text={translate("The amount of commission we earn for this product per every insured person above 18 years.\nSpecifically for healthcare products one order might consist of multiple insured persons above 18 years.")}/>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onValueChange={value => {
									setCommissionConfig({
										...commissionConfig,
										commissionAmountPerBillablePerson: value
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Flat commission amount")}
								variant={"standard"}
								placeholder={"0"}
								value={commissionAmountFixedFee ?? ""}
								InputProps={{
									startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
									endAdornment: <InputTooltip
										text={translate("The fixed amount of commission we earn for selling this product.\nNo matter for how long the product will be active or how many insured people are included in the order, the fixed amount of commission is the amount we earn only for 1 specific order.")}/>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onValueChange={value => {
									setCommissionConfig({
										...commissionConfig,
										commissionAmountFixedFee: value
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Number of months for renewal commission")}
								variant={"standard"}
								placeholder={"0"}
								fractionDigits={0}
								value={renewalCommissionInstallmentNumberOfMonths ?? ""}
								InputLabelProps={{
									shrink: true
								}}
								InputProps={{
									endAdornment: <InputTooltip
										text={translate("This is the duration (in months) for the renewal commission")}/>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onValueChange={value => {
									const numberValue = value ? Number(value) : null;
									setCommissionConfig({
										...commissionConfig,
										renewalCommissionInstallmentNumberOfMonths: numberValue
									});
								}}
							/>
							
							<NumberField
								style={{width: "250px"}}
								label={translate("Renewal commission installment amount")}
								variant={"standard"}
								placeholder={"0"}
								value={renewalCommissionInstallmentAmount ?? ""}
								InputProps={{
									startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
									endAdornment: <InputTooltip
										text={translate("The fixed amount of commission we earn for renewal")}/>
								}}
								inputProps={{
									step: "1",
									lang: "nl-NL",
								}}
								disabled={props.disabled}
								onValueChange={value => {
									setCommissionConfig({
										...commissionConfig,
										renewalCommissionInstallmentAmount: value
									});
								}}
							/>
							<TextField
								variant={"standard"}
								label={translate("Renewal commission installment link")}
								value={renewalCommissionInstallmentAmountLink ?? ""}
								style={{flexGrow: 1}}
								disabled={props.disabled}
								onChange={event => {
									setCommissionConfig({
										...commissionConfig,
										renewalCommissionInstallmentAmountLink: event.target.value
									});
								}}
							/>
							<div>
								<Button
									disabled={props.disabled}
									onClick={() => {
										const newList = [...productCommission.commissionConfigs];
										newList.splice(index, 1);
										props.setProductCommission({...productCommission, commissionConfigs: newList});
									}}
								>{translate("Remove")}</Button>
							</div>
						</Stack>
					</AccordionDetails>
				</Accordion>;
			})}
			<div>
				<IconButton
					color={"primary"}
					disabled={props.disabled}
					onClick={() => {
						const newList = [...productCommission.commissionConfigs, defaultProductCommissionConfig()];
						props.setProductCommission({...productCommission, commissionConfigs: newList});
					}}
				>
					<Add/>
				</IconButton>
			</div>
			
			<Typography variant={"h5"}>Shares</Typography>
			{productCommission.commissionShareConfigs.map((commissionShareConfig, index) => {
				function setCommissionConfig(newConfig: ProductCommissionShareConfig) {
					const newList = [...productCommission.commissionShareConfigs];
					newList[index] = newConfig;
					props.setProductCommission({...productCommission, commissionShareConfigs: newList});
				}
				
				const partnerCommissionFactor = asPercentage(commissionShareConfig.partnerCommissionFactor);
				const partnerFixedAmount = commissionShareConfig.partnerFixedAmount ?? null;
				
				const header = `${translate("From")} ${commissionShareConfig.startDate} ${translate("until")} ${commissionShareUntilIndex.get(commissionShareConfig.startDate) ?? translate("the end of time")}`;
				const showPartnerHierarchy = props.partnerHierarchy && (!commissionShareConfig.subPartnerCommissions || commissionShareConfig.subPartnerCommissions.length === 0);
				let totalPartnerCommissionFactor = commissionShareConfig.partnerCommissionFactor ?? 0;
				if (commissionShareConfig.subPartnerCommissions) {
					for (const item of commissionShareConfig.subPartnerCommissions) {
						if (typeof item.partnerCommissionFactor === "number") {
							totalPartnerCommissionFactor += item.partnerCommissionFactor;
						}
					}
				}
				const kiziTotalPartnerCommissionFactor = asPercentage(1 - totalPartnerCommissionFactor);
				
				return <Accordion key={`share-${index}`}>
					<AccordionSummary>{header}</AccordionSummary>
					<AccordionDetails>
						<Stack spacing={2} direction={"row"}>
							<Stack spacing={2}>
								<DateTimePicker
									textFieldProps={{
										style: {width: "250px"}
									}}
									label={translate("Start Date")}
									variant={"standard"}
									value={parseDateNullable(commissionShareConfig.startDate)}
									inputFormat={GlobalState.userPreferences.inputDateTimePatternOrDefault}
									disabled={props.disabled}
									onValueChange={value => {
										const valueString = value ? DateTimeFormatter.ISO_LOCAL_DATE_TIME.format(value) : null as unknown as string;
										setCommissionConfig({...commissionShareConfig, startDate: valueString});
									}}
								/>
								
								<NumberField
									style={{width: "250px"}}
									label={translate("Partner commission percentage")}
									variant={"standard"}
									placeholder={"0"}
									value={partnerCommissionFactor ?? ""}
									InputLabelProps={{
										shrink: true
									}}
									InputProps={{
										endAdornment: <>
											<InputAdornment position={"end"}>%</InputAdornment>
											<InputTooltip
												text={translate("This is the percentage of the total commission that the applicable partner receives.")}/>
										</>
									}}
									inputProps={{
										step: "1",
										lang: "nl-NL",
										style: {textAlign: "right"}
									}}
									disabled={props.disabled}
									onValueChange={value => {
										const numberValue = value ? Number(value) / 100 : null;
										setCommissionConfig({
											...commissionShareConfig,
											partnerCommissionFactor: numberValue
										});
									}}
								/>
								
								<NumberField
									style={{width: "250px"}}
									label={translate("Partner flat amount")}
									variant={"standard"}
									placeholder={"0"}
									value={partnerFixedAmount ?? ""}
									InputProps={{
										startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
										endAdornment: <InputTooltip
											text={translate("This is a fixed amount of the total commission that the applicable partner receives.")}/>
									}}
									inputProps={{
										step: "1",
										lang: "nl-NL",
									}}
									disabled={props.disabled}
									onValueChange={value => {
										setCommissionConfig({
											...commissionShareConfig,
											partnerFixedAmount: value
										});
									}}
								/>
								
								<div>
									<Button
										key={"remove_commissionShareConfigs"}
										disabled={props.disabled}
										onClick={() => {
											const newList = [...productCommission.commissionShareConfigs];
											newList.splice(index, 1);
											props.setProductCommission({
												...productCommission,
												commissionShareConfigs: newList
											});
										}}
									>REMOVE</Button>
									
									{showPartnerHierarchy &&
										<Button
											key={"add_partnercommission"}
											disabled={props.disabled}
											onClick={() => {
												const productCommissionSubPartnerShareConfig = [...commissionShareConfig.subPartnerCommissions ?? []];
												productCommissionSubPartnerShareConfig.push({partnerName: props.partnerHierarchy?.parent ?? "", partnerCommissionFactor: 0, partnerFixedAmount: 0});
												
												setCommissionConfig({
													...commissionShareConfig,
													subPartnerCommissions: productCommissionSubPartnerShareConfig
												});
											}}
										>Add sub partner commission</Button>
									}
								</div>
							</Stack>
							{commissionShareConfig.subPartnerCommissions && commissionShareConfig.subPartnerCommissions.map((subPartnerCommissions, index) => {
								
								const subPartnerCommissionFactor = asPercentage(subPartnerCommissions.partnerCommissionFactor);
								const subPartnerFixedAmount = subPartnerCommissions.partnerFixedAmount ?? null;
								
								function setPartnerCommissionConfig(newPartnerConfig: SubPartnerCommissions) {
									const newList = [...commissionShareConfig.subPartnerCommissions ?? []];
									newList[index] = newPartnerConfig;
									setCommissionConfig({...commissionShareConfig, subPartnerCommissions: newList});
								}
								
								return <Stack spacing={2} key={"productCommissionSubPartnerShareConfig" + index}>
									<TextField
										label={translate("Partner name")}
										value={subPartnerCommissions.partnerName}
										variant={"standard"}
										disabled
									/>
									<NumberField
										key={"partnerCommissionFactor" + subPartnerCommissions.partnerName}
										style={{width: "250px"}}
										label={translate("Sub Partner commission percentage")}
										variant={"standard"}
										placeholder={"0"}
										value={subPartnerCommissionFactor ?? ""}
										InputLabelProps={{
											shrink: true
										}}
										InputProps={{
											endAdornment: <>
												<InputAdornment position={"end"}>%</InputAdornment>
												<InputTooltip
													text={translate("This is the percentage of the total commission that the applicable partner receives.")}/>
											</>
										}}
										inputProps={{
											step: "1",
											lang: "nl-NL",
											style: {textAlign: "right"}
										}}
										disabled={props.disabled}
										onValueChange={value => {
											const numberValue = value ? Number(value) / 100 : null;
											setPartnerCommissionConfig({
												...subPartnerCommissions,
												partnerCommissionFactor: numberValue
											});
										}}
									/>
									
									<NumberField
										key={"partnerFixedAmount" + subPartnerCommissions.partnerName}
										style={{width: "250px"}}
										label={translate("Partner flat amount")}
										variant={"standard"}
										placeholder={"0"}
										value={subPartnerFixedAmount ?? ""}
										InputProps={{
											startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
											endAdornment: <InputTooltip
												text={translate("This is a fixed amount of the total commission that the applicable partner receives.")}/>
										}}
										inputProps={{
											step: "1",
											lang: "nl-NL",
										}}
										disabled={props.disabled}
										onValueChange={value => {
											setPartnerCommissionConfig({
												...subPartnerCommissions,
												partnerFixedAmount: value
											});
										}}
									/>
								</Stack>;
							})}
							{function () {
								return <Stack spacing={2} key={"productCommissionKiziPartnerShareConfig"}>
									<TextField
										label={translate("Partner name")}
										value={"Kizi"}
										variant={"standard"}
										disabled
									/>
									<NumberField
										key={"KiziPartnerCommissionFactor"}
										style={{width: "250px"}}
										label={translate("Kizi commission percentage")}
										variant={"standard"}
										placeholder={"0"}
										value={kiziTotalPartnerCommissionFactor ?? ""}
										InputLabelProps={{
											shrink: true
										}}
										InputProps={{
											endAdornment: <>
												<InputAdornment position={"end"}>%</InputAdornment>
												<InputTooltip
													text={translate("This is the percentage of the total commission that the applicable partner receives.")}/>
											</>
										}}
										inputProps={{
											step: "1",
											lang: "nl-NL",
											style: {textAlign: "right"}
										}}
										disabled={true}
									/>
									
									<NumberField
										key={"kiziPartnerFixedAmount"}
										style={{width: "250px"}}
										label={translate("Kizi flat amount")}
										variant={"standard"}
										placeholder={"0"}
										value={0}
										InputProps={{
											startAdornment: <InputAdornment position={"start"}>€</InputAdornment>,
											endAdornment: <InputTooltip
												text={translate("This is a fixed amount of the total commission that the applicable partner receives.")}/>
										}}
										inputProps={{
											step: "1",
											lang: "nl-NL",
										}}
										disabled={true}
									/>
								</Stack>;
							}()}
						</Stack>
					</AccordionDetails>
				</Accordion>;
			})}
			<div>
				<IconButton
					color={"primary"}
					disabled={props.disabled}
					onClick={() => {
						const newList = [...productCommission.commissionShareConfigs, defaultProductCommissionShareConfig()];
						props.setProductCommission({...productCommission, commissionShareConfigs: newList});
					}}
				>
					<Add/>
				</IconButton>
			</div>
		</Stack>
	</TabPanel>;
}

function InputTooltip(props: { text: string }) {
	return <Tooltip title={renderText(props.text)} placement={"right"}>
		<InputAdornment position={"end"}><HelpOutline color={"info"}/></InputAdornment>
	</Tooltip>;
}

function computeUntilIndexFromObjects(objects: { startDate: string }[]): Map<string, string | null> {
	return computeUntilIndexFromStrings(objects.map(it => it.startDate));
}

function computeUntilIndexFromStrings(startDates: string[]): Map<string, string | null> {
	const pairs = startDates.map(it => ({label: it, value: parseDate(it)}))
		.sort((a, b) => a.value.compareTo(b.value));
	
	const entries: [string, string | null][] = pairs.map((pair, index) => {
		if (index + 1 === pairs.length)
			return [pair.label, null];
		else
			return [pair.label, pairs[index + 1].label];
	});
	
	return new Map<string, string | null>(entries);
}

function defaultProductCommission(krn: string): ProductCommission {
	return {
		id: crypto.randomUUID(),
		state: "ACTIVE",
		krn: krn,
		commissionConfigs: [],
		commissionShareConfigs: [],
	} as any;
}

function defaultProductCommissionConfig(): ProductCommissionConfig {
	return {
		startDate: defaultConfigStartDate(),
		commissionNetPremiumFactor: null,
		commissionNetPremiumFactorPerBillablePerson: null,
		commissionAmountPerMonth: null,
		numberOfMonths: null,
		commissionAmountPerBillablePerson: null,
		commissionAmountFixedFee: null,
		renewalCommissionInstallmentAmount:null,
		renewalCommissionInstallmentNumberOfMonths:null,
		renewalCommissionInstallmentAmountLink:null
	};
}

function defaultProductCommissionShareConfig(): ProductCommissionShareConfig {
	return {
		startDate: defaultConfigStartDate(),
		partnerFixedAmount: null,
		partnerCommissionFactor: null,
		subPartnerCommissions: null
	};
}

function defaultConfigStartDate(): string {
	return LocalDate.now().atStartOfDay().format(DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm:ss"));
}

export function openProductDetailsForNew(
	krn: string,
	clientName: string,
	productSupplierId: string,
	productSupplierKrn: string,
	externalId: string,
	name: string,
	description: string,
	dataProvider: string,
	productType: string,
) {
	const product = {
		id: crypto.randomUUID(),
		krn,
		clientName,
		productSupplierId,
		productSupplierKrn,
		externalId,
		name,
		description,
		dataProviderKrn: dataProvider,
		productTypeKrn: productType,
		productType: productType.split(":").last(),
		state: "ACTIVE",
	};
	newProduct.set(product);
	openProductDetailsFor(krn);
}

export function openProductDetailsFor(krn: string, tail: string | null = null) {
	window.location.href = linkProductDetailsFor(krn) + (tail ? `/${tail}` : "");
}

export function linkProductDetailsFor(krn: string): string {
	const segments = krn.split(":");
	const tenant = segments[1];
	const supplier = segments[3];
	const [provider, product] = segments[5].split("/");
	return `/products/products/${encodeURIComponent([tenant, supplier, provider, product].join(":"))}`;
}
